import { useCallback, useContext, useEffect, useState } from 'react';

import { AuthContext } from '../../providers/auth-provider';
import type { PlanResponse } from '../../lib/api-client';

export interface DataAccessEntry {
  system: string;
  data: string;
  accessLevel: string;
  expiry: string;
}

interface Props {
  planId: string;
  title: string;
  steps: string[];
  dataAccess: DataAccessEntry[];
  /** Parent callback fired once approval succeeds — parent sends the trigger message. */
  onApproved?: (planId: string) => void;
}

type Display = 'loading' | 'pending' | 'approved' | 'denied' | 'error';

const APPROVED_STATUSES = new Set(['approved', 'executing', 'completed']);
const DENIED_STATUSES = new Set(['denied', 'revised']);

/**
 * Fetches the plan's current server state so reloaded conversations show the
 * correct terminal status without waiting for the user to re-approve. Falls
 * back to `pending` when no auth context is available (tests, SSR, embed pre-
 * auth).
 */
function usePlanStatus(planId: string | undefined) {
  const authCtx = useContext(AuthContext);
  const apiClient = authCtx?.apiClient ?? null;
  const hasFetchable = !!apiClient && !!planId && !planId.startsWith('tool_');
  const [status, setStatus] = useState<Display>(hasFetchable ? 'loading' : 'pending');
  const [plan, setPlan] = useState<PlanResponse | null>(null);

  const refetch = useCallback(() => {
    if (!hasFetchable || !apiClient || !planId) {
      setStatus('pending');
      return;
    }
    setStatus('loading');
    apiClient
      .getPlan(planId)
      .then((p) => {
        setPlan(p);
        if (APPROVED_STATUSES.has(p.status)) setStatus('approved');
        else if (DENIED_STATUSES.has(p.status)) setStatus('denied');
        else setStatus('pending');
      })
      .catch(() => setStatus('error'));
  }, [apiClient, hasFetchable, planId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { status, plan, refetch };
}

export function PlanApprovalCard({ planId, title, steps, dataAccess, onApproved }: Props) {
  const authCtx = useContext(AuthContext);
  const apiClient = authCtx?.apiClient ?? null;
  const { status, plan, refetch } = usePlanStatus(planId);
  const [expanded, setExpanded] = useState(true);
  const [actionStatus, setActionStatus] = useState<Display | null>(null);
  const [error, setError] = useState<string | undefined>();
  const displayStatus = actionStatus ?? status;
  const isPending = displayStatus === 'pending';
  const isLoading = displayStatus === 'loading';

  const handleApprove = useCallback(async () => {
    if (!apiClient || !planId) return;
    setActionStatus('approved');
    setError(undefined);
    try {
      const resp = await apiClient.approvePlan(planId);
      if (resp.trigger_ready) onApproved?.(planId);
    } catch (e: unknown) {
      setActionStatus(null);
      setError(e instanceof Error ? e.message : 'Approval failed');
    }
  }, [apiClient, planId, onApproved]);

  const handleDeny = useCallback(async () => {
    if (!apiClient || !planId) return;
    setActionStatus('denied');
    setError(undefined);
    try {
      await apiClient.denyPlan(planId);
    } catch (e: unknown) {
      setActionStatus(null);
      setError(e instanceof Error ? e.message : 'Denial failed');
    }
  }, [apiClient, planId]);

  const accent = statusAccent(displayStatus);

  return (
    <div
      data-testid="plan-approval-card"
      style={{
        border: `1px solid ${accent.border}`,
        borderLeft: `3px solid ${accent.accent}`,
        borderRadius: 8,
        background: accent.bg,
        marginBottom: 10,
        overflow: 'hidden',
        transition: 'border-color 160ms ease, background 160ms ease',
        animation: isPending ? 'sd-pulse-border 2.4s ease-in-out infinite' : undefined,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
          color: 'inherit',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
        aria-expanded={expanded}
      >
        <span aria-hidden style={{ fontSize: 14 }}>
          {accent.icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 2 }}>
            {statusLabel(displayStatus)}
            {steps.length > 0 && ` · ${steps.length} step${steps.length === 1 ? '' : 's'}`}
            {dataAccess.length > 0 && ` · ${dataAccess.length} system${dataAccess.length === 1 ? '' : 's'}`}
          </div>
        </div>
        <StatusBadge status={displayStatus} />
        <span
          aria-hidden
          style={{
            color: 'var(--text-mute)',
            fontSize: 10,
            marginLeft: 4,
            transition: 'transform 120ms ease',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          ›
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {steps.length > 0 && (
            <Section label="Steps">
              <ol
                style={{
                  margin: 0,
                  paddingLeft: 22,
                  color: 'var(--text)',
                  fontSize: 12,
                  lineHeight: 1.55,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                {steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
            </Section>
          )}

          {dataAccess.length > 0 && (
            <Section label="Data access">
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  overflow: 'hidden',
                  background: 'var(--surface)',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 11,
                  }}
                >
                  <thead>
                    <tr style={{ background: 'var(--bg-alt)' }}>
                      <Th>System</Th>
                      <Th>Data</Th>
                      <Th>Access</Th>
                      <Th>Protection</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataAccess.map((d, i) => (
                      <tr key={i}>
                        <Td>{d.system || '—'}</Td>
                        <Td>{d.data || '—'}</Td>
                        <Td>
                          <span
                            style={{
                              fontSize: 10,
                              padding: '1px 6px',
                              borderRadius: 10,
                              background: 'var(--accent-softer)',
                              color: 'var(--accent)',
                              textTransform: 'capitalize',
                            }}
                          >
                            {d.accessLevel.replace(/[-_]/g, ' ')}
                          </span>
                        </Td>
                        <Td>
                          <span style={{ fontSize: 10, color: 'var(--text-soft)' }}>
                            🔒 Vault-protected
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {displayStatus === 'approved' && plan?.approved_at && (
            <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
              Approved
              {plan.approved_by ? ` by ${plan.approved_by}` : ''} ·{' '}
              {new Date(plan.approved_at).toLocaleString()}
            </div>
          )}

          {displayStatus === 'denied' && plan?.denial_reason && (
            <div style={{ fontSize: 11, color: 'var(--kind-failure)' }}>
              Denied: {plan.denial_reason}
            </div>
          )}

          {error && (
            <div style={{ fontSize: 11, color: 'var(--kind-failure)' }}>
              {error}{' '}
              <button
                type="button"
                onClick={refetch}
                style={{
                  background: 'transparent',
                  border: 0,
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 11,
                  padding: 0,
                  textDecoration: 'underline',
                }}
              >
                Retry status
              </button>
            </div>
          )}

          {(isPending || displayStatus === 'error') && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleDeny}
                style={{
                  padding: '6px 14px',
                  fontSize: 12,
                  border: '1px solid var(--border-strong)',
                  borderRadius: 6,
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 500,
                }}
              >
                Deny
              </button>
              <button
                type="button"
                onClick={handleApprove}
                style={{
                  padding: '6px 14px',
                  fontSize: 12,
                  border: '1px solid var(--accent)',
                  borderRadius: 6,
                  background: 'var(--accent)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 500,
                }}
              >
                Approve
              </button>
            </div>
          )}

          {isLoading && (
            <div style={{ fontSize: 11, color: 'var(--text-mute)', fontStyle: 'italic' }}>
              Checking plan status…
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '5px 8px',
        borderBottom: '1px solid var(--border)',
        fontSize: 10,
        color: 'var(--text-soft)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td
      style={{
        padding: '6px 8px',
        borderTop: '1px solid var(--border)',
        color: 'var(--text)',
        fontSize: 11,
      }}
    >
      {children}
    </td>
  );
}

function StatusBadge({ status }: { status: Display }) {
  const label = statusLabel(status);
  const accent = statusAccent(status);
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 10,
        border: `1px solid ${accent.border}`,
        background: accent.pillBg,
        color: accent.accent,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
}

function statusLabel(status: Display): string {
  switch (status) {
    case 'loading':
      return 'Checking status…';
    case 'pending':
      return 'Awaiting approval';
    case 'approved':
      return 'Approved';
    case 'denied':
      return 'Denied';
    case 'error':
      return 'Status unavailable';
  }
}

function statusAccent(status: Display): {
  icon: string;
  accent: string;
  border: string;
  bg: string;
  pillBg: string;
} {
  switch (status) {
    case 'approved':
      return {
        icon: '✓',
        accent: '#2f855a',
        border: 'rgba(47,133,90,0.35)',
        bg: 'rgba(47,133,90,0.06)',
        pillBg: 'rgba(47,133,90,0.1)',
      };
    case 'denied':
      return {
        icon: '✕',
        accent: '#c0392b',
        border: 'rgba(192,57,43,0.35)',
        bg: 'rgba(192,57,43,0.05)',
        pillBg: 'rgba(192,57,43,0.1)',
      };
    case 'error':
      return {
        icon: '!',
        accent: '#c0392b',
        border: 'rgba(192,57,43,0.35)',
        bg: 'rgba(192,57,43,0.05)',
        pillBg: 'rgba(192,57,43,0.1)',
      };
    case 'loading':
      return {
        icon: '…',
        accent: 'var(--text-mute)',
        border: 'var(--border)',
        bg: 'var(--bg-alt2)',
        pillBg: 'var(--surface)',
      };
    case 'pending':
    default:
      return {
        icon: '◆',
        accent: 'var(--accent)',
        border: 'var(--accent-soft)',
        bg: 'var(--accent-softer)',
        pillBg: 'var(--surface)',
      };
  }
}
